import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Alert, Button, Col, Flex, Form, Input, Row, Select } from 'antd';
import allCountries from 'country-region-data/data.json';
import { collect } from 'collect.js';
import FadeIn from '../FadeIn';
import GooglePlacesSearch from '../form/address/GooglePlacesSearch';
import CountrySelect from '../form/address/CountrySelect';
import GooglePlaceHelpers from '../../services/helpers/GooglePlaceHelpers';
import useApiClient from '../../hooks/api/useApiClient';
import useBusinessProfile from '../../hooks/context/useBusinessProfile';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import useForm from '../../hooks/utility/useForm';
import { placeToLocation, requiresRegion } from '../../views/restaurant-onboarding/locationPhaseHelpers';

const ADDRESS_FIELDS = ['address', 'zip_code', 'city', 'state'];

/**
 * Get Started's first step: the restaurant's name and the address its customers see, both
 * prefilled from creation. Finishing it is confirming them — by staff, Get Started is not
 * opened to owners (Nishita and Artur, 20 Oct) — so the save always stamps the step, whether
 * anything changed or not.
 *
 * - The name is the business profile's display name — shared by every location of the
 *   owner, so a multi-location owner is asked where the change applies. It is saved first,
 *   and only when it changed: the address save is what stamps the step, so a cancelled
 *   name change leaves the step open.
 * - The address is the restaurant's own location (PUT /location): the map pin, the delivery
 *   area and the shop's address. The country is not editable here — it sets the
 *   restaurant's currency, tax and regional settings — so the search stays inside it.
 * - Store staff may not change the address (`canConfirm` false). They see the details
 *   read-only and move on, and the step stays open until sales or an onboarding manager
 *   confirms it.
 */
export default function StoreDetailsStep({ buttonText, moveForward, canConfirm = true }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:StoreDetailsStep' });

  const { company } = useSelector((state) => state.currentCompany);
  const { CompaniesManager } = useApiClient();
  const { getValue, updating, pushUpdates } = useBusinessProfile();
  const { setErrorFeedback, resetFeedback, renderFeedbackLabel } = useFeedbackLabel({ t });

  const { data, update } = useForm({
    display_name: getValue('display_name'),
    address: company?.address ?? null,
    zip_code: company?.zip_code ?? null,
    city: company?.city ?? null,
    state: company?.state ?? null,
    lat: company?.lat ?? null,
    lon: company?.lon ?? null,
  });
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const country = company?.country;
  const showsRegion = requiresRegion(country);
  const regionOptions = useMemo(
    () =>
      collect(collect(allCountries).firstWhere('countryShortCode', country)?.regions ?? [])
        .map((item) => ({ label: item.name, value: item.name }))
        .toArray(),
    [country],
  );

  const isComplete =
    [data.display_name, data.address, data.zip_code, data.city].every((value) => Boolean(value?.trim?.() ?? value)) &&
    (!showsRegion || Boolean(data.state));

  const addressChanged = ADDRESS_FIELDS.some((field) => (data[field] ?? null) !== (company?.[field] ?? null));

  const onPlaceSelected = (place) => {
    const { address, zip_code, city, state, lat, lon } = placeToLocation(place, GooglePlaceHelpers);

    setSearch('');
    // A result without a street number has no address line. The street alone is still the
    // new one, and the empty number is easier to spot than the old address left in place.
    update({ address: address ?? GooglePlaceHelpers.getStreetName(place), zip_code, city, state, lat, lon });
  };

  // A hand-edited address no longer matches the point it had. Null coordinates make the
  // server look the new address up (UpdateCompanyLocationAction), and refuse one it cannot
  // place on the map.
  const edit = (field, value) => update({ [field]: value, lat: null, lon: null });

  const saveLocation = async ({ announce }) => {
    setSaving(true);
    const { success, data: response } = await CompaniesManager.updateLocation({
      address: data.address,
      zip_code: data.zip_code,
      city: data.city,
      ...(data.state ? { state: data.state } : {}),
      // Unchanged, the restaurant keeps its point. Sending a missing one back would make the
      // server geocode an address nobody touched, and refuse the step if it cannot.
      ...(addressChanged ? { lat: data.lat, lon: data.lon } : {}),
      store_details_confirmed: true,
    });
    setSaving(false);

    if (!success) {
      setErrorFeedback(response);
      toast.error(t('toasts.error'));

      return;
    }

    resetFeedback();
    announce && toast.success(t('toasts.saved'));
    moveForward && moveForward();
  };

  const save = () => {
    if (!canConfirm) {
      moveForward && moveForward();

      return;
    }

    if (!isComplete) {
      return;
    }

    if (data.display_name === getValue('display_name')) {
      saveLocation({ announce: true });

      return;
    }

    // The profile save shows its own toast.
    pushUpdates({
      data: { display_name: data.display_name },
      onSuccess: () => saveLocation({ announce: false }),
      onError: ({ data: response }) => setErrorFeedback(response),
    });
  };

  return (
    <FadeIn>
      {renderFeedbackLabel}
      {!canConfirm && (
        <Alert
          type="info"
          showIcon
          title={t('staff_notice')}
          className="mb-md"
          data-testid="store-details-staff-notice"
        />
      )}
      <Form layout="vertical" disabled={!canConfirm}>
        <Form.Item label={t('form.display_name.label')} extra={t('form.display_name.help')} required>
          <Input
            placeholder={t('form.display_name.placeholder')}
            value={data.display_name ?? ''}
            onChange={(e) => update('display_name', e.target.value)}
            data-testid="store-details-name"
          />
        </Form.Item>
        <hr />
        {canConfirm && (
          <Form.Item label={t('form.store_address.label')} extra={t('form.store_address.help')}>
            <GooglePlacesSearch
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPlaceSelected={onPlaceSelected}
              placeholder={t('form.store_address.placeholder')}
              options={{
                // `geometry` carries the point the delivery area and the map pin read.
                fields: ['address_components', 'geometry', 'place_id'],
                types: ['address'],
                componentRestrictions: { country },
              }}
            />
          </Form.Item>
        )}
        <Form.Item label={t('form.address.label')} required>
          <Input
            placeholder={t('form.address.placeholder')}
            value={data.address ?? ''}
            onChange={(e) => edit('address', e.target.value)}
            data-testid="store-details-address"
          />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label={t('form.zip_code.label')} required>
              <Input
                placeholder={t('form.zip_code.placeholder')}
                value={data.zip_code ?? ''}
                onChange={(e) => edit('zip_code', e.target.value)}
                data-testid="store-details-zip-code"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('form.city.label')} required>
              <Input
                placeholder={t('form.city.placeholder')}
                value={data.city ?? ''}
                onChange={(e) => edit('city', e.target.value)}
                data-testid="store-details-city"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={showsRegion ? 12 : 24}>
            <Form.Item label={t('form.country.label')} extra={t('form.country.help')}>
              <CountrySelect value={country} disabled />
            </Form.Item>
          </Col>
          {showsRegion && (
            <Col span={12}>
              <Form.Item label={t('form.state.label')} required>
                <Select
                  value={data.state}
                  options={regionOptions}
                  onChange={(value) => edit('state', value)}
                  showSearch={{ optionFilterProp: 'label' }}
                  placeholder={t('form.state.placeholder')}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
      <Flex justify="end">
        <Button
          type="primary"
          onClick={save}
          loading={saving || updating}
          disabled={canConfirm && !isComplete}
          data-testid="store-details-save"
        >
          {buttonText ?? t('buttons.next')}
        </Button>
      </Flex>
    </FadeIn>
  );
}
